import { GraphQLRequestError } from '../services/graphqlClient';

/**
 * One place that turns a failed permanent delete into text a person can read.
 *
 * The API answers a failed permanent delete with a typed error: NOT_FOUND
 * (the record is gone) or CONFLICT (it still exists but is not archived, or
 * other records still use it), each with its own message and no stack trace.
 * GraphQL carries the type in `extensions.code`; REST carries it as the HTTP
 * status (404 / 409).
 *
 * Only the server's message text is ever shown. a raw transport error can embed
 * the whole request and response as JSON, so only the server's sentence is used; neither is anything that looks like a stack trace.
 */

type DeleteErrorCode = 'NOT_FOUND' | 'CONFLICT';

const STATUS_TO_CODE: Record<number, DeleteErrorCode> = { 404: 'NOT_FOUND', 409: 'CONFLICT' };

const MAX_MESSAGE_LENGTH = 240;

/** True for text that is safe to show: one short plain sentence, no payload, no trace. */
function isReadable(message: unknown): message is string {
  if (typeof message !== 'string') return false;
  const text = message.trim();
  if (!text || text.length > MAX_MESSAGE_LENGTH) return false;
  if (/[\r\n]/.test(text)) return false;
  if (/^\s*[{[]/.test(text) || /"(response|request|errors|stacktrace)"\s*:/.test(text)) return false;
  if (/\bat\s+\S+\s+\(.*:\d+:\d+\)/.test(text)) return false;
  return true;
}

interface ServerError {
  code?: string;
  message?: string;
}

function readServerError(err: unknown): ServerError {
  if (err instanceof GraphQLRequestError) {
    const first = err.errors[0];
    const code = first?.extensions?.code;
    return { code: typeof code === 'string' ? code : undefined, message: first?.message };
  }
  if (err && typeof err === 'object') {
    const { status, message } = err as { status?: unknown; message?: unknown };
    return {
      code: typeof status === 'number' ? STATUS_TO_CODE[status] : undefined,
      message: typeof message === 'string' ? message : undefined,
    };
  }
  return {};
}

/** Friendly text for each typed error, used when the server sent no readable message. */
export function friendlyDeleteText(code: string | undefined, noun: string): string {
  if (code === 'NOT_FOUND') return `This ${noun} no longer exists. Refresh the list to see the latest.`;
  if (code === 'CONFLICT') return `This ${noun} cannot be deleted yet. Archive it first, or remove whatever still uses it.`;
  return `The ${noun} could not be deleted. Please try again.`;
}

/**
 * The message to show for any failed guest action (save, archive, restore):
 * the server's own readable sentence if it sent one, else the fallback. Never
 * graphql-request's `ClientError.message`, which embeds the whole request and
 * response as JSON.
 */
export function readableErrorMessage(err: unknown, fallback: string): string {
  const { message } = readServerError(err);
  if (isReadable(message)) return message.trim();
  return fallback;
}

/**
 * The message to show for a failed permanent delete.
 * @param noun singular name of the record, e.g. "guest" (used when the server sent no readable message)
 */
export function permanentDeleteErrorMessage(err: unknown, noun = 'record'): string {
  const { code, message } = readServerError(err);
  if (isReadable(message)) return message.trim();
  return friendlyDeleteText(code, noun);
}
